import { useMemo, useState } from 'react'
import type { SupplierItem, SupplierItemRow, SupplierLineRef } from '@fitting-room/contracts'
import { ArrowLeftRight, ChevronLeft, Link2, Unlink } from 'lucide-react'
import { Link } from 'react-router'
import { PageHeader } from '../../components/admin/PageHeader'
import { useLinkSupplierItem, useSupplierItems, useUnlinkSupplierItem } from '../../lib/adminQueries'
import { BOUTIQUE_LOCALE, boutiqueDateTime } from '../../lib/boutique'
import { formatDay } from '../../lib/format'
import { useStaffToken } from '../../lib/staffToken'

/**
 * The boutique's special orders against what was ordered from Jovita.
 *
 * Laid out from this side: each special-order or accessory line nothing is
 * linked to yet sits on the left, and its Jovita item is chosen on the right
 * — the likeliest first, every unlinked item one select away — with the
 * Link between them. Read from Jovita's side, the lines nobody ordered were
 * a footnote under a list of items, and an order with no Jovita item stayed
 * unlinked for good without anyone seeing it. The item's details show only
 * once it is chosen, so the list stays readable.
 *
 * A linked line follows the OMS from then on: factory number, ETA, shipped,
 * arrived, refreshed when somebody looks. `docs/22-back-office.md` § Pending.
 */
export function JovitaOrdersPage() {
  const { token } = useStaffToken()
  const data = useSupplierItems(token)
  const body = data.data

  const unlinkedItems = useMemo(() => (body?.items ?? []).filter((row) => !row.linked), [body])
  const linked = useMemo(() => (body?.items ?? []).filter((row) => row.linked), [body])
  const likelyByLine = useMemo(() => likelyItemsByLine(unlinkedItems), [unlinkedItems])
  // Jovita items no unlinked line is a likely match for: ordered for stock, or for an order written before the console.
  const orphanItems = useMemo(() => unlinkedItems.filter((row) => !(body?.unlinkedLines ?? []).some((line) => row.suggestions.some((s) => s.lineId === line.lineId))), [unlinkedItems, body])

  return (
    <div>
      <Link to="/admin/orders" className="st-btn-quiet mb-2 inline-flex items-center gap-1" style={{ paddingInline: 0 }}>
        <ChevronLeft size={14} aria-hidden />
        Orders
      </Link>
      <PageHeader eyebrow="Ordered from Jovita" title="Jovita orders" />

      {data.isError ? (
        <p className="st-alert mb-4">
          {data.error.code === 'supplier_not_connected'
            ? "Jovita isn't connected to the console yet, so special orders are tracked by hand."
            : data.error.message}
        </p>
      ) : null}

      {body ? (
        <p className="st-meta mb-6 max-w-3xl">
          Our special orders on the left; choose each one&rsquo;s item from Jovita on the right, and link them. A linked line
          follows Jovita from then on — factory number, ETA, shipped and arrived. Jovita lists its last {body.windowDays ?? 120}{' '}
          days; read {boutiqueDateTime(body.fetchedAt)}
          {body.dataAsOf ? `, Jovita’s data as of ${boutiqueDateTime(body.dataAsOf)}` : ''}.
          {body.stale ? ' Jovita answered from an older copy than usual.' : ''}
        </p>
      ) : data.isPending ? (
        <p className="st-meta">Loading…</p>
      ) : null}

      {body ? (
        <>
          <section className="mb-10">
            <h2 className="st-eyebrow mb-2">To link · {body.unlinkedLines.length}</h2>
            {body.unlinkedLines.length === 0 ? (
              <p className="st-meta">Every special order on an open order is linked to its Jovita item.</p>
            ) : (
              <>
                <div className="st-meta mb-1 hidden text-xs md:grid md:grid-cols-[minmax(0,1fr)_7rem_minmax(0,1.2fr)] md:gap-4 md:px-4">
                  <span>Our special order</span>
                  <span />
                  <span>Jovita item</span>
                </div>
                <ul className="space-y-2">
                  {body.unlinkedLines.map((line) => (
                    <MatchRow key={line.lineId} line={line} likely={likelyByLine.get(line.lineId) ?? []} items={unlinkedItems} />
                  ))}
                </ul>
              </>
            )}
          </section>

          {body.missing.length > 0 ? (
            <section className="mb-10">
              <h2 className="st-eyebrow mb-2">No longer listed by Jovita · {body.missing.length}</h2>
              <p className="st-meta mb-2 max-w-3xl">
                Linked, but Jovita stopped listing the item — out of its window, or removed. Each is kept as it last was; follow
                it up by hand.
              </p>
              <ul className="space-y-1">
                {body.missing.map((line) => (
                  <li key={line.lineId} className="st-card flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm">
                    <LineSummary line={line} />
                    <span className="st-meta text-xs">
                      Jovita #{line.link.itemNo ?? line.link.itemId} · last {line.link.status ?? 'seen'}
                      {line.link.missingSince ? ` · gone since ${boutiqueDateTime(line.link.missingSince)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <details className="mb-6">
            <summary className="st-eyebrow mb-2 cursor-pointer">Linked · {linked.length}</summary>
            {linked.length === 0 ? (
              <p className="st-meta">Nothing linked yet.</p>
            ) : (
              <ul className="space-y-1">
                {linked.map((row) => (
                  <LinkedRow key={row.item.id} row={row} />
                ))}
              </ul>
            )}
          </details>

          <details className="mb-6">
            <summary className="st-eyebrow mb-2 cursor-pointer">Jovita items no special order here matches · {orphanItems.length}</summary>
            <p className="st-meta mb-2 max-w-3xl">
              Ordered from Jovita with nothing on this side that looks like it: an accessory for the shelf, or an order not
              written in the console. Each can still be chosen on any line above.
            </p>
            {orphanItems.length === 0 ? (
              <p className="st-meta">None.</p>
            ) : (
              <ul className="space-y-1">
                {orphanItems.map((row) => (
                  <li key={row.item.id} className="st-card px-4 py-2 text-sm">
                    {itemLabel(row.item)}
                  </li>
                ))}
              </ul>
            )}
          </details>
        </>
      ) : null}
    </div>
  )
}

interface LikelyItem {
  readonly row: SupplierItemRow
  readonly score: number
  readonly reasons: readonly string[]
}

/** Each line's likely Jovita items, best first — the items' own suggestions, read the other way round. */
function likelyItemsByLine(rows: readonly SupplierItemRow[]): Map<string, LikelyItem[]> {
  const out = new Map<string, LikelyItem[]>()
  for (const row of rows) {
    for (const suggestion of row.suggestions) {
      const list = out.get(suggestion.lineId) ?? []
      list.push({ row, score: suggestion.score, reasons: suggestion.reasons })
      out.set(suggestion.lineId, list)
    }
  }
  for (const list of out.values()) list.sort((a, b) => b.score - a.score)
  return out
}

/**
 * One of our lines, its Jovita item chosen beside it, and the Link between.
 * The likeliest item starts chosen; its reasons and its details show under
 * the choice, and only for the one chosen.
 */
function MatchRow({ line, likely, items }: { readonly line: SupplierLineRef; readonly likely: readonly LikelyItem[]; readonly items: readonly SupplierItemRow[] }) {
  const { token } = useStaffToken()
  const link = useLinkSupplierItem(token)
  const [choice, setChoice] = useState(likely[0]?.row.item.id ?? '')
  const chosen = items.find((row) => row.item.id === choice)
  const reasons = likely.find((l) => l.row.item.id === choice)?.reasons ?? []
  const others = items.filter((row) => !likely.some((l) => l.row.item.id === row.item.id))
  const selectId = `jovita-for-${line.lineId}`

  return (
    <li className="st-card grid items-start gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_7rem_minmax(0,1.2fr)] md:gap-4" data-testid="jovita-match">
      <div className="min-w-0 text-sm">
        <OurLine line={line} />
      </div>

      <div className="order-last md:order-none md:pt-0.5">
        <button
          type="button"
          className="st-btn w-full justify-center"
          disabled={!chosen || link.isPending}
          onClick={() => chosen && link.mutate({ orderId: line.orderId, lineId: line.lineId, itemId: chosen.item.id })}
          title={chosen ? `Link ${line.orderNo} to Jovita #${chosen.item.itemNo ?? chosen.item.id}` : 'Choose a Jovita item first'}
        >
          <Link2 size={14} aria-hidden />
          {link.isPending ? 'Linking…' : 'Link'}
        </button>
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <label className="sr-only" htmlFor={selectId}>
          Jovita item for {line.orderNo} {lineTitle(line)}
        </label>
        <select id={selectId} className="st-select w-full" value={choice} onChange={(event) => setChoice(event.target.value)}>
          <option value="">{likely.length > 0 ? 'Choose a Jovita item…' : 'No likely item — choose one…'}</option>
          {likely.length > 0 ? (
            <optgroup label="Likely">
              {likely.map(({ row }) => (
                <option key={row.item.id} value={row.item.id}>
                  {itemLabel(row.item)}
                </option>
              ))}
            </optgroup>
          ) : null}
          {others.length > 0 ? (
            <optgroup label="Other Jovita items">
              {others.map((row) => (
                <option key={row.item.id} value={row.item.id}>
                  {itemLabel(row.item)}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
        {chosen ? (
          <div className="rounded-[3px] border px-3 py-2" style={{ borderColor: 'var(--st-rule)' }}>
            {reasons.length > 0 ? <p className="st-ok mb-1 text-xs">{reasons.join(' · ')}</p> : null}
            <ItemFacts item={chosen.item} />
          </div>
        ) : null}
        {link.isError ? <p className="st-alert text-sm">{link.error.message}</p> : null}
      </div>
    </li>
  )
}

/** Our line, in the words the order page uses, its order one click away. */
function OurLine({ line }: { readonly line: SupplierLineRef }) {
  const facts = lineFacts(line)
  return (
    <>
      <div className="font-medium">
        {lineTitle(line)}
        {facts.length > 0 ? <span className="st-meta font-normal"> · {facts.join(' · ')}</span> : null}
      </div>
      <div className="st-meta text-xs">
        <Link to={`/admin/orders/${line.orderId}`} className="underline-offset-2 hover:underline">
          {line.orderNo}
        </Link>{' '}
        · {line.customerName}
      </div>
    </>
  )
}

/** A Jovita item in one line, for a select: who and what first, then Jovita's number. */
function itemLabel(item: SupplierItem): string {
  return [
    item.brideName,
    item.style ?? item.pbfNo ?? 'No style',
    item.size ? `size ${item.size}` : null,
    item.partnerRef ? `PO# ${item.partnerRef}` : null,
    item.weddingDate ? `wedding ${formatDay(item.weddingDate, BOUTIQUE_LOCALE)}` : null,
    `Jovita #${item.itemNo ?? '—'}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** What Jovita says about an item, under the choice. */
function ItemFacts({ item }: { readonly item: SupplierItem }) {
  const progress = [
    item.status,
    item.shippingStatus,
    item.shipDate ? `ships ${formatDay(item.shipDate, BOUTIQUE_LOCALE)}` : null,
    item.trackingNo ? `FedEx ${item.trackingNo}` : null,
  ].filter(Boolean)
  return (
    <div className="min-w-0 text-sm" data-testid="jovita-item-facts">
      {item.brideName ? <div className="font-medium">{item.brideName}</div> : null}
      <div className={item.brideName ? 'tabular-nums' : 'font-medium tabular-nums'}>
        {item.style ?? item.pbfNo ?? '—'}
        {item.size ? <span className="st-meta"> · size {item.size}</span> : null}
        {item.color ? <span className="st-meta"> · {item.color}</span> : null}
      </div>
      <div className="st-meta text-xs">
        {item.itemType ?? 'Item'} · Jovita #{item.itemNo ?? '—'}
        {item.factoryNo ? ` · factory ${item.factoryNo}` : ''}
        {item.orderSubmitDate ? ` · ordered ${formatDay(item.orderSubmitDate, BOUTIQUE_LOCALE)}` : ''}
        {item.weddingDate ? ` · wedding ${formatDay(item.weddingDate, BOUTIQUE_LOCALE)}` : ''}
      </div>
      {item.partnerRef ? <div className="st-meta text-xs">PO# {item.partnerRef}</div> : null}
      {progress.length > 0 ? <div className="text-xs">{progress.join(' · ')}</div> : null}
    </div>
  )
}

/**
 * What a line is, the way the shop names a gown: by its style number. The
 * description is the fallback only — the ClickUp import wrote every gown line
 * as "Gown" with the style beside it, so a list of descriptions was a list of
 * "Gown"s nobody could tell apart. A line with no style number says so.
 */
function lineTitle(line: Pick<SupplierLineRef, 'styleCode' | 'description' | 'merchandise'>): string {
  const group = line.merchandise === 'accessory' ? ' (accessory)' : line.merchandise === 'shoes' ? ' (shoes)' : ''
  if (line.styleCode) return `${line.styleCode}${group}`
  return `${line.description}${group} · no style #`
}

/** The rest of what tells two of one customer's lines apart: the size and the wedding. */
function lineFacts(line: Pick<SupplierLineRef, 'size' | 'weddingDate'>): string[] {
  return [line.size ? `size ${line.size}` : null, line.weddingDate ? `wedding ${formatDay(line.weddingDate, BOUTIQUE_LOCALE)}` : null].filter(
    (fact): fact is string => fact !== null,
  )
}

function LineSummary({ line }: { readonly line: SupplierLineRef }) {
  const facts = lineFacts(line)
  return (
    <span className="min-w-0">
      <Link to={`/admin/orders/${line.orderId}`} className="font-medium underline-offset-2 hover:underline">
        {line.orderNo}
      </Link>{' '}
      · {line.customerName} · <span className="font-medium">{lineTitle(line)}</span>
      {facts.length > 0 ? <span className="st-meta"> · {facts.join(' · ')}</span> : null}
    </span>
  )
}

/** A line and its Jovita item, side by side, with the way to take them apart between. */
function LinkedRow({ row }: { readonly row: SupplierItemRow }) {
  const { token } = useStaffToken()
  const unlink = useUnlinkSupplierItem(token)
  const line = row.linked!
  return (
    <li className="st-card grid items-center gap-3 px-4 py-2 md:grid-cols-[minmax(0,1fr)_7rem_minmax(0,1.2fr)] md:gap-4">
      <div className="min-w-0 text-sm">
        <OurLine line={line} />
        <div className="st-meta text-xs">{line.fulfillment.replace(/_/g, ' ')}</div>
      </div>
      <div className="order-last md:order-none">
        <button
          type="button"
          className="st-btn-ghost w-full justify-center"
          disabled={unlink.isPending}
          onClick={() => unlink.mutate({ orderId: line.orderId, lineId: line.lineId })}
          title="Take this line off the Jovita item"
        >
          <Unlink size={14} aria-hidden />
          Unlink
        </button>
        {unlink.isError ? <p className="st-alert mt-1 text-xs">{unlink.error.message}</p> : null}
      </div>
      <div className="flex min-w-0 items-start gap-2 text-sm">
        <ArrowLeftRight size={14} aria-hidden className="st-meta mt-1 hidden shrink-0 md:block" />
        <span className="min-w-0">{itemLabel(row.item)}</span>
      </div>
    </li>
  )
}
